/**
 * Domain Migration Service
 * Handles the transition from path-based URLs to subdomain-based URLs
 */
import DomainUtils from "../utils/DomainUtils";
import appConfig from "../config/AppConfig";
import StorageEngine from "../utils/StorageEngine";

class DomainMigrationService {
    /**
     * Initialize domain migration service
     * Should be called on app startup
     */
    static initialize() {
        // Only run if subdomain mode is enabled
        if (!appConfig.ENABLE_SUBDOMAIN_MODE) {
            console.log('Subdomain mode is disabled');
            return;
        }

        console.log('DomainMigrationService: Initializing...');


        // Update stored network ID based on current domain
        this.updateStoredNetworkId();

        // Log current domain status for debugging
        console.log('Domain status:', DomainUtils.getDebugInfo());
    }

    /**
     * Handle redirect from legacy path-based URLs to subdomain URLs
     */
    static handleLegacyUrlRedirect() {
        try {
            const redirected = DomainUtils.redirectToSubdomainIfNeeded(
                appConfig.SUBDOMAIN_REDIRECT_IMMEDIATE
            );

            if (redirected) {
                console.log('DomainMigrationService: Redirecting to subdomain URL');
                // Redirect will happen, no further processing needed
                return true;
            }
        } catch (error) {
            console.error('Error handling legacy URL redirect:', error);
        }

        return false;
    }

    /**
     * Update stored network ID based on current domain detection
     */
    static updateStoredNetworkId() {
        try {
            const currentSchoolId = DomainUtils.getCurrentSchoolId();

            if (currentSchoolId) {
                // Load existing stored data
                const storedObject = StorageEngine(appConfig.LAST_VISITED_NETWORK_ID_KEY).load() || {};

                // Update with current school ID info
                const updatedData = {
                    ...storedObject,
                    shortId: currentSchoolId,
                    currentSchoolId: currentSchoolId,
                    lastDetectedMode: DomainUtils.isSubdomainMode() ? 'subdomain' : 'path',
                    lastUpdated: new Date().toISOString()
                };

                // Save updated data
                StorageEngine(appConfig.LAST_VISITED_NETWORK_ID_KEY).save(updatedData);

                console.log('DomainMigrationService: Updated stored network ID:', {
                    schoolId: currentSchoolId,
                    mode: updatedData.lastDetectedMode
                });
            }
        } catch (error) {
            console.error('Error updating stored network ID:', error);
        }
    }

    /**
     * Check if current URL format needs migration
     * @returns {Object} Migration status information
     */
    static checkMigrationStatus() {
        const pathSchoolId = DomainUtils.getSchoolIdFromPath();
        const subdomainSchoolId = DomainUtils.getSubDomain();
        const isSubdomainMode = DomainUtils.isSubdomainMode();

        return {
            needsMigration: pathSchoolId && !subdomainSchoolId,
            currentMode: isSubdomainMode ? 'subdomain' : 'path',
            pathSchoolId,
            subdomainSchoolId,
            recommendedAction: pathSchoolId && !subdomainSchoolId ? 'redirect' : 'none'
        };
    }

    /**
     * Force redirect to subdomain format
     * @param {string} schoolId - School identifier
     * @param {boolean} preserveUrl - Whether to preserve current path and query
     */
    static forceRedirectToSubdomain(schoolId = null, preserveUrl = true) {
        try {
            const targetSchoolId = schoolId || DomainUtils.getSchoolIdFromPath() || DomainUtils.getCurrentSchoolId();

            if (!targetSchoolId) {
                console.warn('Cannot redirect: no school ID available');
                return false;
            }

            let targetPath = '/rec/';

            if (preserveUrl) {
                const currentPath = window.location.pathname;
                const searchParams = window.location.search;
                const hashParams = window.location.hash;

                // Remove school ID from path if it exists
                const cleanPath = currentPath.replace(new RegExp(`^/rec/${targetSchoolId}`), '/rec');

                targetPath = cleanPath + searchParams + hashParams;
            }

            const newUrl = DomainUtils.buildSubdomainUrl(targetSchoolId, targetPath);

            console.log('DomainMigrationService: Force redirecting to:', newUrl);

            if (appConfig.SUBDOMAIN_REDIRECT_IMMEDIATE) {
                window.location.href = newUrl;
            } else {
                window.location.replace(newUrl);
            }

            return true;
        } catch (error) {
            console.error('Error forcing redirect to subdomain:', error);
            return false;
        }
    }

    /**
     * Get current migration recommendations
     * @returns {Object} Migration recommendations
     */
    static getMigrationRecommendations() {
        const status = this.checkMigrationStatus();
        const recommendations = [];

        if (status.needsMigration) {
            recommendations.push({
                type: 'redirect',
                message: 'Current URL uses legacy format. Consider redirecting to subdomain format.',
                action: 'redirect',
                priority: 'high'
            });
        }

        if (status.currentMode === 'path' && DomainUtils.getSchoolIdFromPath()) {
            recommendations.push({
                type: 'modernize',
                message: 'URL could be modernized to use subdomain format.',
                action: 'offer_redirect',
                priority: 'medium'
            });
        }

        return {
            status,
            recommendations
        };
    }

    /**
     * Handle network change in subdomain-aware way
     * @param {Object} newNetworkData - New network data
     */
    static handleNetworkChange(newNetworkData) {
        try {
            const schoolId = newNetworkData.shortId || newNetworkData.currentSchoolId;

            if (!schoolId) {
                console.warn('Cannot handle network change: no school ID provided');
                return false;
            }

            // Update stored data
            const updatedData = {
                shortId: schoolId,
                currentSchoolId: schoolId,
                lastDetectedMode: DomainUtils.isSubdomainMode() ? 'subdomain' : 'path',
                lastUpdated: new Date().toISOString(),
                ...newNetworkData
            };

            StorageEngine(appConfig.LAST_VISITED_NETWORK_ID_KEY).save(updatedData);

            // If we're in subdomain mode but the domain doesn't match, redirect
            if (DomainUtils.isSubdomainMode()) {
                const currentSubdomainId = DomainUtils.getSubDomain();
                if (currentSubdomainId !== schoolId) {
                    console.log('Network change requires subdomain redirect');
                    return this.forceRedirectToSubdomain(schoolId, true);
                }
            }

            return true;
        } catch (error) {
            console.error('Error handling network change:', error);
            return false;
        }
    }
}

export default DomainMigrationService;
